import React from 'react';
import { cloneDeep, find, isNil } from 'lodash';
import moment from 'moment';

// React view component to render
import { flyout as FlyoutView } from 'ui/views/widgets/flyout';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { FLYOUT_TEMPLATE } from 'constants/common';
import { flyoutMessage as FlyoutMessage } from 'ui/views/generic/flyoutMessage';

//connect to Store to fetch values
import { connect } from 'react-redux';
import { REDUCER } from 'constants/entity-names';

// table view templates
const FLYOUT_VIEW_TEMPLATES = {
    [ FLYOUT_TEMPLATE.DEFAULT ]: FlyoutMessage,
    [ FLYOUT_TEMPLATE.WEBSOCKET_DEBUG ]: FlyoutView
};

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { Flyout } from 'ui/containers/Flyout';
 *
 * // render
 * <Flyout propName='propValue'/>
 */
class _Flyout extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isOpen: false,
            widgets: []
        };

        this.__bind();
    }

    /**
     * @description Handles binding of methods and listeners
     */
    __bind = () => {
        log.debug( 'Flyout.__bind()' );

        // listen for websocket triggers
        MessageBus.on( `${ MESSAGE_BUS_EVENTS.ADD_TO_FLYOUT }`, this.handleWebSocketTriggers, this );
        
        // listen for widget updates
        MessageBus.on( `${ MESSAGE_BUS_EVENTS.UPDATE_FLYOUT }`, this.handleWidgetTriggers, this );
    }

    /**
     * Handles addition of updates to state on WebSocket triggers
     * @param {object} message Customised WebSocket data object received via MessageBus
     */
    handleWebSocketTriggers = ( { id, widgetName: name, requestType: type } ) => {
        log.debug( 'Flyout.handleWebSocketTriggers()' );

        const state = cloneDeep( this.state );

        if ( this.props.theme === FLYOUT_TEMPLATE.WEBSOCKET_DEBUG && ! state.isOpen ) { // open when the websocket message is recieved in debug mode
            state.isOpen = true;
        }

        state.widgets.push( { id, name, type, updated: false, timeStamp: moment() } );

        this.setState( state );
    }

    /**
     * Handles updating widget's updated status
     * @param {object} param0 Object containing widget name and its updated status
     */
    handleWidgetTriggers = ( { id } ) => {
        log.debug( 'Flyout.handleWidgetTriggers()' );

        const state = cloneDeep( this.state );
        const widget = find( state.widgets, { id } );

        if ( ! isNil( widget ) ) {
            widget.updated = true;
        }

        if ( this.props.theme !== FLYOUT_TEMPLATE.WEBSOCKET_DEBUG && !state.isOpen ) { //if not debug mode view and flyput not open
            state.isOpen = true;
        }

        this.setState( state );
    }

    /**
     *  @description Handles closing of flyout
     */
    handleFlyoutClose = () => {
        log.debug( 'Flyout.handleFlyoutClose()' );

        // clear widgets state only after flyout is hidden
        this.setState( { isOpen: false }, () => setTimeout( () => this.setState( { widgets: [] } ), 500 ) );
    }

    /**
     *  @description Handles closing of specific Notification flyout
     */
     handleSpecificFlyoutClose = ( id ) => {
        log.debug( 'Flyout.handleSpecificFlyoutClose()' );

        const state = cloneDeep( this.state );
        const widget = find( state.widgets, { id } );

        if ( ! isNil( widget ) ) {
            widget.updated = true;
        }

        if ( this.props.theme !== FLYOUT_TEMPLATE.WEBSOCKET_DEBUG && !state.isOpen ) { //if not debug mode view and flyput not open
            state.isOpen = true;
        }

        this.setState( state );

        // clear widgets state only after flyout is hidden
        this.setState( { isOpen: false }, () => setTimeout( () => this.setState( { widgets: [] } ), 500 ) );
    }

    /**
     * @description Handles unbinding of methods and listeners
     */
    __unbind = () => {
        log.debug( 'Flyout.__unbind()' );

        MessageBus.off( `${ MESSAGE_BUS_EVENTS.ADD_TO_FLYOUT }`, this.handleWebSocketTriggers, this );
        MessageBus.off( `${ MESSAGE_BUS_EVENTS.UPDATE_FLYOUT }`, this.handleWidgetTriggers, this );
    }

    /**
     * @description When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.__unbind();
    }

    /**
     * @description Render a React view component
     */
    render() {

        // eslint-disable-next-line no-shadow
        const FlyoutView = FLYOUT_VIEW_TEMPLATES[ this.props.theme ];

        return (
            <FlyoutView
                { ...this.state }
                onCloseClick={ this.handleFlyoutClose }
                appContext = { this.props.appContext }
            />
        );
    }
}

// map state to props
const mapStateToProps = ( state ) => {
    return {
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// set display name
_Flyout.displayName = 'Flyout';

// set default props
_Flyout.defaultProps = {
    theme: 'default'
};

// prop types
_Flyout.propTypes = {};

// return component with acccess to Redux store
export const Flyout = connect( mapStateToProps, null )( _Flyout );
